import {
  choiceDraftFromLegacy,
  isChoiceAnswerEmpty,
  isChoiceDraftTextInvalid,
  isMultiSelectSurveyQuestion,
  meetsMinSelections,
  normalizeConflictingSelection,
  routingDestinationsFor,
} from './choice-answer'
import { CHOICE_TEXT_RESPONSE_MESSAGE, isChoiceTextTargetSelected } from './choice-text-response'
import { isRankingAnswerComplete } from './ranking-order'
import { isDateAnswerEmpty } from './date-answer'
import { isOpenTextAnswerEmpty } from './text-answer'
import type { ProviderResponseState } from './types'
import type { SurveyDraftAnswer, SurveyQuestion } from './survey-question.types'

export interface SurveyAnswerPayload {
  questionId: string
  rawText?: string
  structuredValue?: string | string[] | number
  durationSeconds?: number
}

export { buildSavePayload } from './survey-answer-payload'

const CHOICE_TYPES = new Set<SurveyQuestion['type']>([
  'multiple_choice',
  'checkbox',
  'dropdown',
])

function hydrateChoiceFromStored(
  question: SurveyQuestion,
  structured: string | string[] | number | undefined,
  rawText: string | undefined,
): SurveyDraftAnswer {
  const otherText = rawText?.trim() ? rawText : ''
  if (isMultiSelectSurveyQuestion(question) && Array.isArray(structured)) {
    const selectedIds = normalizeConflictingSelection(structured, routingDestinationsFor(question))
    return { selectedIds, otherText }
  }
  if (typeof structured === 'string' && structured) {
    return { selectedIds: [structured], otherText }
  }
  return { selectedIds: [], otherText }
}

export function answersMapFromResponse(
  response: ProviderResponseState,
  questionsById?: Map<string, SurveyQuestion>,
): Map<string, SurveyDraftAnswer> {
  const map = new Map<string, SurveyDraftAnswer>()
  for (const a of response.answers) {
    const q = questionsById?.get(a.questionId)
    if (q && CHOICE_TYPES.has(q.type)) {
      const structured = a.structuredValue as string | string[] | undefined
      map.set(
        a.questionId,
        hydrateChoiceFromStored(q, structured, a.rawText),
      )
      continue
    }
    if (a.rawText != null && a.rawText !== '') {
      map.set(a.questionId, a.rawText)
    } else if (a.structuredValue != null) {
      map.set(a.questionId, a.structuredValue as SurveyDraftAnswer)
    }
  }
  return map
}

export function draftAnswerForQuestion(
  map: Map<string, SurveyDraftAnswer>,
  questionId: string,
): SurveyDraftAnswer {
  return map.get(questionId) ?? null
}

export function isAnswerEmpty(question: SurveyQuestion, draft: SurveyDraftAnswer): boolean {
  if (draft == null) return true
  if (CHOICE_TYPES.has(question.type)) {
    return isChoiceAnswerEmpty(question, draft)
  }
  if (question.type === 'ranking') {
    return !isRankingAnswerComplete(
      Array.isArray(draft) ? draft : null,
      question.options.length,
    )
  }
  if (question.type === 'rating' || question.type === 'likert' || question.type === 'slider') {
    return typeof draft !== 'number' || !Number.isFinite(draft)
  }
  if (
    question.type === 'short_text' ||
    question.type === 'long_text' ||
    question.type === 'video'
  ) {
    return isOpenTextAnswerEmpty(question, typeof draft === 'string' ? draft : null)
  }
  if (question.type === 'date') {
    return isDateAnswerEmpty(typeof draft === 'string' ? draft : null)
  }
  if (typeof draft === 'string') return draft.trim() === ''
  if (Array.isArray(draft)) return draft.length === 0
  if (typeof draft === 'number') return false
  return false
}

export const SURVEY_REQUIRED_QUESTION_MESSAGE =
  'This question is required. Please provide an answer before continuing.'

export function canProceedToNext(question: SurveyQuestion, draft: SurveyDraftAnswer): boolean {
  if (CHOICE_TYPES.has(question.type) && draft != null && isChoiceDraftTextInvalid(question, draft)) {
    return false
  }
  if (!question.mandatory) return true
  if (!meetsMinSelections(question, draft)) return false
  return !isAnswerEmpty(question, draft)
}

export function applyClearedQuestionIds(
  map: Map<string, SurveyDraftAnswer>,
  clearedQuestionIds: string[] | undefined,
): Map<string, SurveyDraftAnswer> {
  if (!clearedQuestionIds?.length) return map
  const next = new Map(map)
  for (const id of clearedQuestionIds) {
    next.delete(id)
  }
  return next
}

export function answerUsesOtherText(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): boolean {
  if (!CHOICE_TYPES.has(question.type)) return false
  const { selectedIds } = choiceDraftFromLegacy(question, draft)
  return isChoiceTextTargetSelected(question, selectedIds)
}

/** Inline message for a blocked Next — specify-text message when that is the cause. */
export function surveyNextBlockedMessage(
  question: SurveyQuestion | null,
  draft: SurveyDraftAnswer,
): string {
  if (question && CHOICE_TYPES.has(question.type) && draft != null && isChoiceDraftTextInvalid(question, draft)) {
    return CHOICE_TEXT_RESPONSE_MESSAGE
  }
  return SURVEY_REQUIRED_QUESTION_MESSAGE
}
